/**
 * Task Creation Constants
 * Universal categories, tools, timelines for the new task creation experience
 */

// ============================================================================
// TASK UNIVERSES — Visual category system for ages 8-66
// ============================================================================

export type TaskUniverse =
  | 'create' | 'build' | 'design' | 'automate' | 'learn'
  | 'produce' | 'manage' | 'market' | 'entertain' | 'brand'
  | 'research' | 'other';

export interface UniverseEntry {
  id: TaskUniverse;
  emoji: string;
  label: string;
  subtitle: string;
  color: string;
  glow: string;
  examples: string[];
  placeholder: string;
}

export const TASK_UNIVERSES: UniverseEntry[] = [
  {
    id: 'create',
    emoji: '\uD83C\uDFAC',
    label: 'Create',
    subtitle: 'Videos, images, audio, writing',
    color: '#FF6B6B',
    glow: 'rgba(255, 107, 107, 0.15)',
    examples: ['YouTube video', 'Blog post', 'Podcast episode', 'AI-generated art'],
    placeholder: 'What content are you making? Style, length, format...',
  },
  {
    id: 'build',
    emoji: '\uD83D\uDEE0\uFE0F',
    label: 'Build',
    subtitle: 'Apps, websites, bots, APIs',
    color: '#4ECDC4',
    glow: 'rgba(78, 205, 196, 0.15)',
    examples: ['Web app', 'Mobile app', 'Discord bot', 'API integration'],
    placeholder: 'What should this app/site do? Key features and goals...',
  },
  {
    id: 'design',
    emoji: '\uD83C\uDFA8',
    label: 'Design',
    subtitle: 'Graphics, UI/UX, branding, 3D',
    color: '#A855F7',
    glow: 'rgba(168, 85, 247, 0.15)',
    examples: ['Logo design', 'App mockup', 'Social media template', '3D model'],
    placeholder: 'What should it look like? Brand colors, style, dimensions...',
  },
  {
    id: 'automate',
    emoji: '\u26A1',
    label: 'Automate',
    subtitle: 'Workflows, pipelines, bots',
    color: '#F59E0B',
    glow: 'rgba(245, 158, 11, 0.15)',
    examples: ['n8n workflow', 'CI/CD pipeline', 'Email automation', 'Cron job'],
    placeholder: 'What process do you want to automate? Steps involved...',
  },
  {
    id: 'learn',
    emoji: '\uD83D\uDCDA',
    label: 'Learn',
    subtitle: 'Tutorials, courses, experiments',
    color: '#3B82F6',
    glow: 'rgba(59, 130, 246, 0.15)',
    examples: ['Learn Python', 'AI course notes', 'Study group project'],
    placeholder: 'What are you trying to learn? What do you want to understand...',
  },
  {
    id: 'produce',
    emoji: '\uD83C\uDF9E\uFE0F',
    label: 'Produce',
    subtitle: 'Edit, mix, render, publish',
    color: '#EC4899',
    glow: 'rgba(236, 72, 153, 0.15)',
    examples: ['Video edit in Premiere', 'Audio mix', 'After Effects animation'],
    placeholder: 'What are you editing/producing? Source material, final output...',
  },
  {
    id: 'manage',
    emoji: '\uD83D\uDDA5\uFE0F',
    label: 'Manage',
    subtitle: 'Servers, databases, DevOps',
    color: '#10B981',
    glow: 'rgba(16, 185, 129, 0.15)',
    examples: ['Deploy to AWS', 'Database migration', 'Docker setup'],
    placeholder: 'What needs to be set up, fixed, or migrated...',
  },
  {
    id: 'market',
    emoji: '\uD83D\uDCE2',
    label: 'Market',
    subtitle: 'Social media, SEO, campaigns',
    color: '#F97316',
    glow: 'rgba(249, 115, 22, 0.15)',
    examples: ['Instagram campaign', 'SEO optimization', 'Ad creative'],
    placeholder: 'What are you promoting? Target audience, channels, goals...',
  },
  {
    id: 'entertain',
    emoji: '\uD83C\uDFAE',
    label: 'Entertain',
    subtitle: 'Gaming, streaming, music',
    color: '#8B5CF6',
    glow: 'rgba(139, 92, 246, 0.15)',
    examples: ['Game mod', 'Twitch overlay', 'Music production', 'Animation'],
    placeholder: 'What entertainment are you creating? Platform, genre, style...',
  },
  {
    id: 'brand',
    emoji: '\u2728',
    label: 'Brand',
    subtitle: 'Identity, packaging, strategy',
    color: '#14B8A6',
    glow: 'rgba(20, 184, 166, 0.15)',
    examples: ['Brand kit', 'Style guide', 'Packaging design'],
    placeholder: 'What brand elements are you creating? Vision, values, audience...',
  },
  {
    id: 'research',
    emoji: '\uD83D\uDD2C',
    label: 'Research',
    subtitle: 'Data, AI experiments, testing',
    color: '#6366F1',
    glow: 'rgba(99, 102, 241, 0.15)',
    examples: ['Data analysis', 'ML experiment', 'A/B test'],
    placeholder: 'What are you researching? Hypothesis, data sources, methods...',
  },
  {
    id: 'other',
    emoji: '\uD83C\uDF1F',
    label: 'Something Else',
    subtitle: 'Your unique task',
    color: '#64748B',
    glow: 'rgba(100, 116, 139, 0.15)',
    examples: ['Anything goes!'],
    placeholder: 'Describe what you need to get done...',
  },
];

// ============================================================================
// EXPERIENCE LEVELS
// ============================================================================

export type TaskExperience = 'explorer' | 'maker' | 'pro' | 'expert';

export interface ExperienceEntry {
  id: TaskExperience;
  emoji: string;
  label: string;
  desc: string;
}

export const EXPERIENCE_LEVELS: ExperienceEntry[] = [
  { id: 'explorer', emoji: '\uD83C\uDF31', label: 'Explorer', desc: "I'm learning as I go" },
  { id: 'maker',    emoji: '\uD83D\uDD27', label: 'Maker',    desc: 'I know the basics' },
  { id: 'pro',      emoji: '\uD83D\uDE80', label: 'Pro',      desc: 'I do this regularly' },
  { id: 'expert',   emoji: '\u2B50',        label: 'Expert',   desc: 'I could teach this' },
];

// ============================================================================
// TIMELINE OPTIONS
// ============================================================================

export type TaskTimeline = 'asap' | 'this-week' | 'this-month' | 'no-rush' | 'custom';

export interface TimelineEntry {
  id: TaskTimeline;
  emoji: string;
  label: string;
  desc: string;
}

export const TIMELINES: TimelineEntry[] = [
  { id: 'asap',       emoji: '\uD83D\uDD25', label: 'ASAP',        desc: 'Need help now' },
  { id: 'this-week',  emoji: '\uD83D\uDCC5', label: 'This Week',   desc: 'Wrapping up soon' },
  { id: 'this-month', emoji: '\uD83D\uDDD3\uFE0F', label: 'This Month',  desc: 'Steady progress' },
  { id: 'no-rush',    emoji: '\uD83C\uDF0A', label: 'No Rush',     desc: 'Ongoing / flexible' },
  { id: 'custom',     emoji: '\uD83D\uDCCC', label: 'Pick a Date', desc: 'Set a deadline' },
];

// ============================================================================
// COLLABORATION STYLES
// ============================================================================

export type CollabStyle = 'solo' | 'help-wanted' | 'team' | 'mentor';

export interface CollabEntry {
  id: CollabStyle;
  emoji: string;
  label: string;
  desc: string;
}

export const COLLAB_STYLES: CollabEntry[] = [
  { id: 'solo',        emoji: '\uD83C\uDFAF', label: 'Solo Project',  desc: "I'm doing this myself" },
  { id: 'help-wanted', emoji: '\uD83E\uDD1D', label: 'Help Wanted',   desc: 'Looking for collaborators' },
  { id: 'team',        emoji: '\uD83D\uDC65', label: 'Team Task',     desc: 'Part of a group effort' },
  { id: 'mentor',      emoji: '\uD83E\uDDED', label: 'Need a Mentor', desc: "I'm learning & want guidance" },
];

// ============================================================================
// TOOL DATABASE
// ============================================================================

export type ToolCategory =
  | 'ai-assistant' | 'ai-media' | 'design' | 'video' | 'audio'
  | 'dev-tools' | 'cloud' | 'data' | 'automation' | 'productivity'
  | 'social' | 'other';

export interface ToolEntry {
  id: string;
  name: string;
  category: ToolCategory;
  popular: boolean;
  color: string;
}

export const TOOL_CATEGORIES: { id: ToolCategory; label: string }[] = [
  { id: 'ai-assistant', label: 'AI Assistants' },
  { id: 'ai-media',     label: 'AI Media' },
  { id: 'design',       label: 'Design' },
  { id: 'video',        label: 'Video' },
  { id: 'audio',        label: 'Audio' },
  { id: 'dev-tools',    label: 'Dev Tools' },
  { id: 'cloud',        label: 'Cloud' },
  { id: 'data',         label: 'Data' },
  { id: 'automation',   label: 'Automation' },
  { id: 'productivity', label: 'Productivity' },
  { id: 'social',       label: 'Social' },
];

export const POPULAR_TOOLS: ToolEntry[] = [
  // AI Assistants
  { id: 'cursor',       name: 'Cursor',          category: 'ai-assistant', popular: true, color: '#7C3AED' },
  { id: 'claude',       name: 'Claude',           category: 'ai-assistant', popular: true, color: '#D97706' },
  { id: 'chatgpt',      name: 'ChatGPT',          category: 'ai-assistant', popular: true, color: '#10A37F' },
  { id: 'copilot',      name: 'GitHub Copilot',    category: 'ai-assistant', popular: true, color: '#238636' },
  { id: 'gemini',       name: 'Gemini',            category: 'ai-assistant', popular: true, color: '#4285F4' },
  { id: 'perplexity',   name: 'Perplexity',        category: 'ai-assistant', popular: true, color: '#20B2AA' },

  // AI Media
  { id: 'elevenlabs',   name: 'ElevenLabs',       category: 'ai-media', popular: true, color: '#000000' },
  { id: 'midjourney',   name: 'Midjourney',       category: 'ai-media', popular: true, color: '#FFFFFF' },
  { id: 'runway',       name: 'Runway',            category: 'ai-media', popular: true, color: '#6366F1' },
  { id: 'suno',         name: 'Suno',              category: 'ai-media', popular: true, color: '#F43F5E' },
  { id: 'dall-e',       name: 'DALL-E',            category: 'ai-media', popular: true, color: '#10A37F' },
  { id: 'stable-diff',  name: 'Stable Diffusion',  category: 'ai-media', popular: true, color: '#A855F7' },
  { id: 'kling',        name: 'Kling AI',          category: 'ai-media', popular: true, color: '#FF6B6B' },
  { id: 'pika',         name: 'Pika',              category: 'ai-media', popular: true, color: '#EC4899' },

  // Design
  { id: 'canva',        name: 'Canva',            category: 'design', popular: true, color: '#00C4CC' },
  { id: 'figma',        name: 'Figma',            category: 'design', popular: true, color: '#F24E1E' },
  { id: 'photoshop',    name: 'Photoshop',        category: 'design', popular: true, color: '#31A8FF' },
  { id: 'illustrator',  name: 'Illustrator',      category: 'design', popular: true, color: '#FF9A00' },
  { id: 'blender',      name: 'Blender',          category: 'design', popular: true, color: '#E87D0D' },
  { id: 'framer',       name: 'Framer',           category: 'design', popular: true, color: '#0055FF' },

  // Video
  { id: 'premiere',     name: 'Premiere Pro',     category: 'video', popular: true, color: '#9999FF' },
  { id: 'aftereffects', name: 'After Effects',    category: 'video', popular: true, color: '#9999FF' },
  { id: 'capcut',       name: 'CapCut',           category: 'video', popular: true, color: '#00F0FF' },
  { id: 'davinci',      name: 'DaVinci Resolve',  category: 'video', popular: true, color: '#FF6B6B' },
  { id: 'envato',       name: 'Envato',           category: 'video', popular: true, color: '#81B441' },

  // Audio
  { id: 'audacity',     name: 'Audacity',         category: 'audio', popular: true, color: '#0000CC' },
  { id: 'logic-pro',    name: 'Logic Pro',        category: 'audio', popular: true, color: '#555555' },
  { id: 'fl-studio',    name: 'FL Studio',        category: 'audio', popular: true, color: '#FF8800' },

  // Dev Tools
  { id: 'vscode',       name: 'VS Code',          category: 'dev-tools', popular: true, color: '#007ACC' },
  { id: 'github',       name: 'GitHub',            category: 'dev-tools', popular: true, color: '#FFFFFF' },
  { id: 'docker',       name: 'Docker',            category: 'dev-tools', popular: true, color: '#2496ED' },
  { id: 'postman',      name: 'Postman',           category: 'dev-tools', popular: true, color: '#FF6C37' },
  { id: 'windsurf',     name: 'Windsurf',          category: 'dev-tools', popular: true, color: '#6366F1' },

  // Cloud
  { id: 'aws',          name: 'AWS',              category: 'cloud', popular: true, color: '#FF9900' },
  { id: 'firebase',     name: 'Firebase',         category: 'cloud', popular: true, color: '#FFCA28' },
  { id: 'vercel',       name: 'Vercel',           category: 'cloud', popular: true, color: '#FFFFFF' },
  { id: 'netlify',      name: 'Netlify',          category: 'cloud', popular: true, color: '#00C7B7' },
  { id: 'supabase',     name: 'Supabase',         category: 'cloud', popular: true, color: '#3ECF8E' },
  { id: 'cloudflare',   name: 'Cloudflare',       category: 'cloud', popular: true, color: '#F38020' },

  // Data
  { id: 'sql',          name: 'SQL',              category: 'data', popular: true, color: '#336791' },
  { id: 'mongodb',      name: 'MongoDB',          category: 'data', popular: true, color: '#47A248' },
  { id: 'postgres',     name: 'PostgreSQL',       category: 'data', popular: true, color: '#336791' },
  { id: 'redis',        name: 'Redis',            category: 'data', popular: true, color: '#DC382D' },

  // Automation
  { id: 'n8n',          name: 'n8n',              category: 'automation', popular: true, color: '#EA4B71' },
  { id: 'zapier',       name: 'Zapier',           category: 'automation', popular: true, color: '#FF4A00' },
  { id: 'make',         name: 'Make',             category: 'automation', popular: true, color: '#6D00CC' },
  { id: 'gh-actions',   name: 'GitHub Actions',   category: 'automation', popular: true, color: '#2088FF' },

  // Productivity
  { id: 'notion',       name: 'Notion',           category: 'productivity', popular: true, color: '#FFFFFF' },
  { id: 'slack',        name: 'Slack',            category: 'productivity', popular: true, color: '#4A154B' },
  { id: 'linear',       name: 'Linear',           category: 'productivity', popular: true, color: '#5E6AD2' },
  { id: 'jira',         name: 'Jira',             category: 'productivity', popular: true, color: '#0052CC' },

  // Social
  { id: 'youtube',      name: 'YouTube',          category: 'social', popular: true, color: '#FF0000' },
  { id: 'instagram',    name: 'Instagram',        category: 'social', popular: true, color: '#E4405F' },
  { id: 'tiktok',       name: 'TikTok',           category: 'social', popular: true, color: '#000000' },
  { id: 'twitter',      name: 'X / Twitter',      category: 'social', popular: true, color: '#FFFFFF' },
];

// ============================================================================
// FORM TYPES
// ============================================================================

export interface ToolSelection {
  id: string;
  name: string;
  category: string;
  isCustom?: boolean;
}

export interface TaskLink {
  title: string;
  url: string;
}

export interface NewTaskForm {
  // Step 1
  universe: TaskUniverse | '';
  title: string;
  goal: string;
  experience: TaskExperience;
  // Step 2
  tools: ToolSelection[];
  description: string;
  links: TaskLink[];
  tags: string[];
  // Step 3
  timeline: TaskTimeline;
  customDeadline: string;
  visibility: 'public' | 'private';
  collabStyle: CollabStyle;
  saveToDashboard: boolean;
}

export const INITIAL_FORM: NewTaskForm = {
  universe: '',
  title: '',
  goal: '',
  experience: 'maker',
  tools: [],
  description: '',
  links: [],
  tags: [],
  timeline: 'this-week',
  customDeadline: '',
  visibility: 'public',
  collabStyle: 'help-wanted',
  saveToDashboard: true,
};
